import { useState, useEffect } from 'react';
import { Play, Pause, Music, SkipBack, SkipForward, Rewind, FastForward, X, Volume2 } from 'lucide-react';
import axios from 'axios';

export default function UnifiedPlayer() {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPaused, setIsPaused] = useState(true);
  const [trackType, setTrackType] = useState(null); // 'spotify' or 'mp3'
  const [spotifyConnected, setSpotifyConnected] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(100); // Volume state (0-100)

  useEffect(() => {
    // Listen for track updates from window
    window.updatePlayerTrack = (track, type) => {
      console.log('UnifiedPlayer: Updating track to', track.name, 'type:', type);
      setCurrentTrack(track);
      setTrackType(type);
      setIsPaused(false);
      // Reset progress when new track loads
      setCurrentTime(0);
      setDuration(0);
      setProgress(0);
      // Set default volume based on track type
      if (type === 'mp3') {
        setVolume(35); // MP3s at 35%
      } else if (type === 'spotify' || type === 'radio') {
        setVolume(20); // Spotify and Radio at 20%
      }
    };

    window.updatePlayerPaused = (paused) => {
      setIsPaused(paused);
    };

    window.clearPlayerTrack = () => {
      console.log('UnifiedPlayer: Clearing track');
      setCurrentTrack(null);
      setTrackType(null);
      setIsPaused(true);
      setCurrentTime(0);
      setDuration(0);
      setProgress(0);
    };

    window.setSpotifyConnected = (connected) => {
      setSpotifyConnected(connected);
    };

    window.updateVolumeSlider = (vol) => {
      setVolume(vol);
      console.log(`UnifiedPlayer: Volume slider updated to ${vol}%`);
    };

    return () => {
      delete window.updatePlayerTrack;
      delete window.updatePlayerPaused;
      delete window.clearPlayerTrack;
      delete window.setSpotifyConnected;
      delete window.updateVolumeSlider;
    };
  }, []);

  // Update progress for MP3/Radio playback
  useEffect(() => {
    let interval;
    
    if ((trackType === 'mp3' || trackType === 'radio') && window.currentMP3Audio) {
      // Always run interval for MP3/Radio, check pause state inside
      interval = setInterval(() => {
        const audio = window.currentMP3Audio;
        if (audio) {
          // For radio streams, duration is often Infinity or NaN
          const audioDuration = audio.duration;
          const isLiveStream = !audioDuration || !isFinite(audioDuration) || isNaN(audioDuration);
          
          if (isLiveStream) {
            // For live radio, just show current time without progress
            setCurrentTime(audio.currentTime);
            setDuration(0);
            setProgress(0);
          } else {
            setCurrentTime(audio.currentTime);
            setDuration(audioDuration);
            setProgress((audio.currentTime / audioDuration) * 100);
          }
          
          // Update pause state from audio element
          if (audio.paused !== isPaused) {
            setIsPaused(audio.paused);
          }
        }
      }, 100);
    } else if (trackType === 'spotify' && window.spotifyPlayer) {
      // Always run interval for Spotify, check pause state inside
      interval = setInterval(async () => {
        try {
          const state = await window.spotifyPlayer.getCurrentState();
          if (state) {
            setCurrentTime(state.position / 1000);
            setDuration(state.duration / 1000);
            setProgress((state.position / state.duration) * 100);
            
            // Update pause state from Spotify state
            if (state.paused !== isPaused) {
              setIsPaused(state.paused);
            }
          }
        } catch (err) {
          console.log('Error getting Spotify state:', err);
        }
      }, 500);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [trackType]);

  const togglePlay = () => {
    if (trackType === 'spotify' && window.spotifyPlayer) {
      window.spotifyPlayer.togglePlay();
    } else if ((trackType === 'mp3' || trackType === 'radio') && window.currentMP3Audio) {
      if (isPaused) {
        window.currentMP3Audio.play();
        setIsPaused(false);
      } else {
        window.currentMP3Audio.pause();
        setIsPaused(true);
      }
    }
  };

  const seekMP3 = (seconds) => {
    if (window.currentMP3Audio) {
      window.currentMP3Audio.currentTime = Math.max(0, window.currentMP3Audio.currentTime + seconds);
    }
  };

  const stopFloorMusic = async () => {
    console.log('=== STOPPING FLOOR MUSIC ===');
    
    // Check if radio was playing before floor music started
    const radioStation = window.currentRadioStation;
    const wasRadioPlaying = radioStation && window.mp3PlaybackActive;
    
    // Stop MP3 immediately
    if (window.currentMP3Audio) {
      window.currentMP3Audio.pause();
      window.currentMP3Audio.currentTime = 0;
      window.currentMP3Audio = null;
    }
    
    // Clear MP3 active flag
    window.mp3PlaybackActive = false;
    
    // Clear player track
    if (window.clearPlayerTrack) {
      window.clearPlayerTrack();
    }
    
    // Resume radio if it was playing before floor music started
    if (wasRadioPlaying && window.playRadioStation) {
      console.log('Resuming radio after Stop button...');
      setTimeout(() => {
        window.playRadioStation(radioStation);
      }, 500);
      return;
    }
    
    // Otherwise, resume Spotify background music
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      
      // Get Spotify player
      if (window.spotifyPlayer) {
        console.log('Resuming Spotify after Stop button...');
        await window.spotifyPlayer.resume();
        
        // Fade volume back to 20% (background music default)
        if (window.fadeSpotifyVolume) {
          await window.fadeSpotifyVolume(0.20, 2000);
        } else {
          // Fallback - set volume directly
          await window.spotifyPlayer.setVolume(0.20);
        }
        
        // Update volume slider to 20%
        setVolume(20);
        
        console.log('Spotify resumed at 20% volume');
      }
    } catch (error) {
      console.log('Error resuming Spotify:', error);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseInt(e.target.value);
    setVolume(newVolume);
    
    // Update actual audio volume
    if ((trackType === 'mp3' || trackType === 'radio') && window.currentMP3Audio) {
      window.currentMP3Audio.volume = newVolume / 100;
      console.log(`Audio volume set to ${newVolume}%`);
    } else if (trackType === 'spotify' && window.spotifyPlayer) {
      window.spotifyPlayer.setVolume(newVolume / 100).then(() => {
        console.log(`Spotify volume set to ${newVolume}%`);
      });
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e) => {
    // Don't allow seeking for live radio streams
    if (trackType === 'radio') return;
    
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    
    if (trackType === 'mp3' && window.currentMP3Audio) {
      window.currentMP3Audio.currentTime = percentage * window.currentMP3Audio.duration;
    } else if (trackType === 'spotify' && window.spotifyPlayer) {
      const seekPosition = percentage * (duration * 1000);
      window.spotifyPlayer.seek(seekPosition);
    }
  };

  // Show player if:
  // 1. There's an active track (Spotify or MP3), OR
  // 2. Spotify is connected (ready to play)
  const hasTrack = currentTrack && trackType;
  const shouldShowPlayer = hasTrack || spotifyConnected;
  
  // Only hide if there's absolutely nothing to show
  if (!shouldShowPlayer) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-[#1656A4] border-t-2 border-[#ff3131] p-3 z-50" data-testid="unified-player">
      <div className="max-w-7xl mx-auto">
        {/* Progress Bar */}
        {hasTrack && (
          <div className="mb-2">
            <div 
              className="w-full h-1 bg-white/20 rounded-full cursor-pointer hover:h-1.5 transition-all"
              onClick={handleProgressClick}
              data-testid="progress-bar"
            >
              <div 
                className="h-full bg-[#ff3131] rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between text-xs opacity-80 mt-1">
              <span data-testid="current-time">{formatTime(currentTime)}</span>
              <span data-testid="duration">{formatTime(duration)}</span>
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          {/* Track Info */}
          <div className="flex items-center gap-4 flex-1" data-testid="track-info">
            {hasTrack ? (
              <>
                {currentTrack.image ? (
                  <img 
                    src={currentTrack.image} 
                    alt={currentTrack.name}
                    className="w-12 h-12 rounded-lg"
                    data-testid="track-image"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center">
                    <Music size={24} className="text-white/50" />
                  </div>
                )}
                <div>
                  <div className="font-bold text-base" data-testid="track-name">{currentTrack.name}</div>
                  <div className="text-xs opacity-80" data-testid="track-type">
                    {trackType === 'spotify' ? 'Background Music' : trackType === 'radio' ? 'Live Radio' : 'Floor Music'}
                    {currentTrack.artist && ` • ${currentTrack.artist}`}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center">
                  <Music size={24} className="text-white/50" />
                </div>
                <div>
                  <div className="font-bold text-base">Ready to Play</div>
                  <div className="text-xs opacity-80">Click a playlist to start music</div>
                </div>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            {hasTrack && trackType === 'mp3' && (
              <>
                {/* Rewind 10s */}
                <button
                  onClick={() => seekMP3(-10)}
                  className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors"
                  data-testid="rewind-10-button"
                  title="Rewind 10 seconds"
                >
                  <div className="flex items-center">
                    <Rewind size={20} />
                    <span className="text-xs ml-1">10</span>
                  </div>
                </button>

                {/* Rewind 5s */}
                <button
                  onClick={() => seekMP3(-5)}
                  className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors"
                  data-testid="rewind-5-button"
                  title="Rewind 5 seconds"
                >
                  <div className="flex items-center">
                    <Rewind size={18} />
                    <span className="text-xs ml-1">5</span>
                  </div>
                </button>
              </>
            )}

            {/* Play/Pause */}
            {hasTrack && (
              <button
                onClick={togglePlay}
                className="bg-[#ff3131] hover:bg-[#ff5050] p-3 rounded-full transition-colors"
                data-testid="play-pause-button"
              >
                {isPaused ? <Play size={24} fill="white" /> : <Pause size={24} fill="white" />}
              </button>
            )}

            {hasTrack && trackType === 'mp3' && (
              <>
                {/* Forward 5s */}
                <button
                  onClick={() => seekMP3(5)}
                  className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors"
                  data-testid="forward-5-button"
                  title="Forward 5 seconds"
                >
                  <div className="flex items-center">
                    <span className="text-xs mr-1">5</span>
                    <FastForward size={18} />
                  </div>
                </button>

                {/* Forward 10s */}
                <button
                  onClick={() => seekMP3(10)}
                  className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors"
                  data-testid="forward-10-button"
                  title="Forward 10 seconds"
                >
                  <div className="flex items-center">
                    <span className="text-xs mr-1">10</span>
                    <FastForward size={20} />
                  </div>
                </button>
                
                {/* Stop Floor Music Button */}
                <button
                  onClick={stopFloorMusic}
                  className="bg-[#ff3131] hover:bg-[#ff5050] px-3 py-2 rounded-full transition-colors flex items-center gap-2 ml-2"
                  data-testid="stop-floor-music-button"
                  title="Stop Floor Music"
                >
                  <X size={18} />
                  <span className="text-sm font-semibold">Stop</span>
                </button>
              </>
            )}
          </div>

          {/* Volume Control */}
          {hasTrack && (
            <div className="flex items-center gap-2 ml-4" data-testid="volume-control">
              <Volume2 size={20} className="text-white" />
              
              {/* Quick volume buttons */}
              <button
                onClick={() => {
                  setVolume(20);
                  if ((trackType === 'mp3' || trackType === 'radio') && window.currentMP3Audio) {
                    window.currentMP3Audio.volume = 0.20;
                  } else if (trackType === 'spotify' && window.spotifyPlayer) {
                    window.spotifyPlayer.setVolume(0.20);
                  }
                }}
                className="bg-white/10 hover:bg-white/20 px-2 py-1 rounded text-xs font-semibold transition-colors"
                data-testid="volume-20-button"
                title="Set volume to 20%"
              >
                20%
              </button>
              
              <button
                onClick={() => {
                  setVolume(35);
                  if ((trackType === 'mp3' || trackType === 'radio') && window.currentMP3Audio) {
                    window.currentMP3Audio.volume = 0.35;
                  } else if (trackType === 'spotify' && window.spotifyPlayer) {
                    window.spotifyPlayer.setVolume(0.35);
                  }
                }}
                className="bg-white/10 hover:bg-white/20 px-2 py-1 rounded text-xs font-semibold transition-colors"
                data-testid="volume-35-button"
                title="Set volume to 35%"
              >
                35%
              </button>
              
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={handleVolumeChange}
                className="w-24 h-1 rounded-lg appearance-none cursor-pointer volume-slider"
                style={{
                  background: `linear-gradient(to right, #ff3131 0%, #ff3131 ${volume}%, rgba(255, 255, 255, 0.2) ${volume}%, rgba(255, 255, 255, 0.2) 100%)`
                }}
                data-testid="volume-slider"
                title={`Volume: ${volume}%`}
              />
              <span className="text-xs font-semibold min-w-[2.5rem]" data-testid="volume-percentage">
                {volume}%
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
